import React from 'react';
import { WORKSPACE_PANELS } from '../constants';
import { tUi } from '../utils/uiText';

function PanelToggle({ panel, active, onToggle, disabled }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(panel.id)}
      disabled={disabled}
      className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
        active
          ? 'bg-teal-500/20 text-teal-300 border border-teal-500/40'
          : 'bg-gray-800/80 text-gray-400 border border-transparent hover:text-gray-200 hover:bg-gray-800'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
      title={panel.description}
    >
      {panel.label}
    </button>
  );
}

export default function WorkspaceSidebar({
  uiLanguage = 'en',
  panels,
  onTogglePanel,
  onShowAll,
  onHideAll,
  isPlatformAdmin,
  canManageOrgUsers = false,
  nationStateConsentBlock,
  childrenByPanel,
}) {
  const visiblePanels = WORKSPACE_PANELS.filter((panel) => {
    if (panel.superadminOnly) return isPlatformAdmin;
    if (panel.orgAdminOnly) return canManageOrgUsers;
    if (panel.adminOnly) return isPlatformAdmin;
    return true;
  });

  return (
    <aside className="w-full md:w-[360px] lg:w-[400px] xl:w-[420px] shrink-0 flex flex-col min-h-0 border-r border-teal-500/20 bg-gray-950">
      <div className="shrink-0 px-4 py-3 border-b border-teal-500/10">
        <div className="flex items-center justify-between gap-2 mb-2">
          <h2 className="text-sm font-semibold text-teal-400">{tUi(uiLanguage, 'workspaceTitle')}</h2>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={onShowAll}
              className="text-[11px] text-gray-400 hover:text-teal-300 px-1.5"
            >
              {tUi(uiLanguage, 'workspaceShowAll')}
            </button>
            <span className="text-gray-600">|</span>
            <button
              type="button"
              onClick={onHideAll}
              className="text-[11px] text-gray-400 hover:text-teal-300 px-1.5"
            >
              {tUi(uiLanguage, 'workspaceHideAll')}
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          {tUi(uiLanguage, 'workspaceHint')}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {visiblePanels.map((panel) => (
            <PanelToggle
              key={panel.id}
              panel={panel}
              active={Boolean(panels[panel.id])}
              onToggle={onTogglePanel}
            />
          ))}
        </div>
      </div>

      <div className="app-scroll-pane p-4 space-y-4">
        {nationStateConsentBlock}
        {visiblePanels.map((panel) => {
          if (!panels[panel.id]) return null;
          const content = childrenByPanel[panel.id];
          if (!content) return null;
          return (
            <section key={panel.id} className="scroll-mt-4">
              {content}
            </section>
          );
        })}
        {!visiblePanels.some((panel) => panels[panel.id]) && !nationStateConsentBlock && (
          <p className="text-sm text-gray-500 text-center py-8 px-2">
            {tUi(uiLanguage, 'workspaceEmpty')}
          </p>
        )}
      </div>
    </aside>
  );
}
